import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog } from "@/components/Dialog";
import type { CopyTradeLeader } from "@/api/copy-trade";

interface CopyTradeSubscribeDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leader: CopyTradeLeader | null;
  isSubmitting?: boolean;
  onSubmit: (payload: {
    fixedUsdAmount: number;
    takeProfitPercent?: number;
    stopLossPercent?: number;
  }) => Promise<void>;
}

export const CopyTradeSubscribeDrawer: React.FC<
  CopyTradeSubscribeDrawerProps
> = ({ open, onOpenChange, leader, isSubmitting, onSubmit }) => {
  const [amount, setAmount] = useState(100);
  const [tp, setTp] = useState("");
  const [sl, setSl] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leader) return;
    await onSubmit({
      fixedUsdAmount: amount,
      takeProfitPercent: tp ? Number(tp) : undefined,
      stopLossPercent: sl ? Number(sl) : undefined,
    });
  };

  if (!leader) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Subscribe to ${leader.nickname}`}
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        <p className="text-sm text-text-secondary">
          Copy new positions opened by this trader using a fixed USDT margin on
          your connected exchange. Leverage matches the lead trader.
        </p>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-text-secondary">
              Fixed margin
            </p>
            <span className="text-xs text-text-secondary">{amount} USDT</span>
          </div>
          <input
            type="number"
            min={1}
            required
            className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || 1)}
          />
          <input
            type="range"
            min={1}
            max={Math.max(500, amount)}
            value={Math.min(amount, Math.max(500, amount))}
            onChange={(e) => setAmount(Number(e.target.value))}
            className="w-full accent-accent"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-text-secondary">
              Take Profit %
            </p>
            <input
              type="number"
              min={0}
              step="0.1"
              className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent"
              value={tp}
              onChange={(e) => setTp(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-text-secondary">
              Stop Loss %
            </p>
            <input
              type="number"
              min={0}
              step="0.1"
              className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent"
              value={sl}
              onChange={(e) => setSl(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 rounded-xl bg-accent text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Start copying
        </button>
      </form>
    </Dialog>
  );
};
